import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { PagesContainerComponent } from '../components/pages-container/pages-container.component';
import { PageContainerService } from '../components/pages-container/services/page-container.service';
import { PageContainerConfigurations } from '../components/pages-container/interfaces/PageContainerConfiguration.interface';
import {
  NavigationItem,
  RoutePath,
} from '../components/pages-container/interfaces/navigationList.interface';
import { PermissionsService } from '../auth/services/permession.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { AuthenticationService } from '../auth/services/authentication.service';
import { ToastService } from '../components/toast/toast.service';
import { routes } from 'src/app/shared/common/routes.consatnt';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, PagesContainerComponent],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MainLayoutComponent {
  navigationList: NavigationItem[] = [];
  routePath: RoutePath[] = [];
  configurations: PageContainerConfigurations = {
    homeButton: {
      title: 'Dashboard',
      routeUrl: this.permissionsService.hasPermission('ServiceProviderHomeView')
        ? '/dashboard'
        : '',
      iconClass: 'icon-house',
    },
    settingRoute: '',
    logoUrl: 'assets/images/HeaderAdroitLogo.svg',
    profile: {
      profileImg: 'assets/images/Avatars.png',
      profileRoute: '',
      userName: 'User Name',
    },
    hasHeaderFilters: false,
    hasNotificationSection: false,
    hasTools: false,
  };
  signedIn: boolean = false;

  isDispatchRoute = false;
  isDashboardRoute = false;

  constructor(
    readonly pageContainerService: PageContainerService,
    readonly permissionsService: PermissionsService,
    readonly _sharedService: SharedService,
    readonly authenticationService: AuthenticationService,
    readonly _toastrService: ToastService,
    private readonly router: Router,
  ) {
    this.initializeRoutePath();
    this.initializeNavigationList();
  }

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.isDispatchRoute = this.router.url.includes('/dispatch');
    this.isDashboardRoute = this.router.url.includes('/dashboard');

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isDispatchRoute = this.router.url.includes('/dispatch');
        this.isDashboardRoute = this.router.url.includes('/dashboard');
      });
  }

  private initializeRoutePath(): void {
    this.routePath = [
      {
        url: '/dispatch/monitoring-center/rides',
        routePath: [1, 6],
      },
      {
        url: '/rides/ride-simulator',
        routePath: [1, 7],
      },
      {
        url: '/accounting/my-payouts',
        routePath: [2, 8],
      },
      {
        url: '/accounting/drivers-weekly-payouts',
        routePath: [2, 9],
      },
      {
        url: '/accounting/drivers-payout-statements',
        routePath: [2, 10],
      },
      {
        url: '/accounting/payout-dispute',
        routePath: [2, 11],
      },
      {
        url: '/routing/ride-blueprint-management',
        routePath: [3, 12],
      },
      {
        url: '/routing/ride-blueprint-offers',
        routePath: [3, 13],
      },
      {
        url: '/drivers/leads',
        routePath: [4, 14],
      },
      {
        url: '/drivers/drivers-and-monitors',
        routePath: [4, 15],
      },
      {
        url: '/drivers/drivers-data-integrity',
        routePath: [4, 16],
      },
      {
        url: '/reports/arrival-time-report',
        routePath: [5, 17],
      },
      {
        url: '/vehicles-equipment/vehicles',
        routePath: [18, 19],
      },
      {
        url: '/vehicles-equipment/equipment-types',
        routePath: [18, 20],
      },
      {
        url: '/vehicles-equipment/equipment-assignments',
        routePath: [18, 21],
      },
    ];

    this.pageContainerService.setRoutePath(this.routePath);
  }

  private initializeNavigationList(): void {
    const rawNavigationList: NavigationItem[] = routes;
    this.navigationList = this.filterNavigationList(rawNavigationList);

    this.pageContainerService.setNavigationList(this.navigationList);
  }

  private filterNavigationList(items: NavigationItem[]): NavigationItem[] {
    return items
      .map(item => {
        // Recursively process children
        const filteredChildren = this.filterNavigationList(item.children || []);

        // Check if the current item has permissions
        const parentHasPermission =
          item.permissions &&
          this.permissionsService.hasAnyPermission(item.permissions);

        // Retain item if:
        // - It has its own permissions
        // - It has children with valid permissions (filteredChildren is not empty)
        const redirectUrl = this.permissionsService.getFirstNavigationUrl(
          item.id,
        );
        if (parentHasPermission || filteredChildren.length > 0) {
          return {
            ...item,
            routeUrl: redirectUrl == '' ? item.routeUrl : redirectUrl,
            children: filteredChildren, // Include only filtered children
          };
        }

        // Exclude items without permissions or valid children
        return null;
      })
      .filter(item => item !== null); // Remove null items
  }

  private isValidGoogleMapsKey(key: string): boolean {
    // Google Maps API keys are typically alphanumeric with dashes or underscores, and at least 30 chars long
    return /^[A-Za-z0-9-_]{30,}$/.test(key);
  }

  public loadScript(name: string) {
    return new Promise<void>((resolve, reject) => {
      let script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = name;
      document.getElementsByTagName('head')[0].appendChild(script);
      resolve();
    });
  }
}
